import React, { useState, useEffect, useRef } from 'react';
import './Targets.css';
import ConfirmModal from './ConfirmModal';
import { FI_PRODUCT_DEFAULTS, normalizeFiProducts } from '../utils/fiProducts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Format currency with $ and commas
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '$0';
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

// Doc fee is an exact dollar-and-cents amount (e.g. $499.00), unlike the
// whole-dollar monthly targets above.
const formatCurrencyCents = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '$0.00';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// The doc fee started out as a single new-bike setting, `{ enabled, amount }`.
// Dealerships saved under that shape are read as new-only so the page edits —
// and saves back — the two-tier form without losing what they had configured.
const normalizeDocFee = (raw) => {
  const tier = (t) => ({
    enabled: !!(t && t.enabled),
    amount: Number(t && t.amount) || 0
  });

  if (!raw) return { new: tier(null), used: tier(null) };

  const legacy = raw.new === undefined && raw.used === undefined;
  return {
    new: tier(legacy ? raw : raw.new),
    used: tier(legacy ? null : raw.used)
  };
};

export default function Targets({ user, managedOwner }) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState(null);
  const [frontEndTargets, setFrontEndTargets] = useState(null);
  const [fixedOpsTargets, setFixedOpsTargets] = useState(null);
  const [dealershipSettings, setDealershipSettings] = useState({
    performanceYearMode: 'CALENDAR',
    fiscalYearStartMonth: 1,
    fiscalYearStartDay: 1,
    demoMode: false
  });
  const [originalSettings, setOriginalSettings] = useState({
    performanceYearMode: 'CALENDAR',
    fiscalYearStartMonth: 1,
    fiscalYearStartDay: 1,
    demoMode: false
  });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('frontend'); // 'frontend' or 'fixedops'
  const [saveMessage, setSaveMessage] = useState(null);
  const [metricsFrequency, setMetricsFrequency] = useState('monthly'); // 'daily', 'weekly', or 'monthly'
  // Doc fee amounts are held as their own display strings so each can show
  // formatted ("$499.00") when idle and raw ("499") while being typed. New and
  // used are configured separately, so there is one string per tier.
  const [docFeeInputs, setDocFeeInputs] = useState({ new: '$0.00', used: '$0.00' });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [pendingSave, setPendingSave] = useState(null);
  const [transactionsRatio, setTransactionsRatio] = useState(20); // Default 20 transactions per bike
  const [transactionsToSitDownsRatio, setTransactionsToSitDownsRatio] = useState(5); // Default 5 transactions per sit-down
  const [transactionsToGreetsRatio, setTransactionsToGreetsRatio] = useState(2); // Default 2 transactions per greet

  // Activity funnel ratios (per unit sold) — editable. Standard funnel 10:4:3:2:1.
  const [greetsRatio, setGreetsRatio] = useState(10); // Default 10 greets per sale
  const [sitDownsRatio, setSitDownsRatio] = useState(4); // Default 4 sit-downs per sale
  const [testRidesRatio, setTestRidesRatio] = useState(3); // Default 3 test rides per sale
  const [writeUpsRatio, setWriteUpsRatio] = useState(2); // Default 2 write-ups per sale

  const DEFAULT_ACTIVITY_RATIOS = { greets: 10, sitDowns: 4, testRides: 3, writeUps: 2 };

  // Industry-standard benchmarks for the Profit Summary transaction and open-RO
  // rows. Dealerships start here and can set their own; clearing a field puts
  // the default back rather than leaving a meaningless zero benchmark.
  // Mirrors backend/src/constants/benchmarks.ts.
  const DEFAULT_BENCHMARKS = {
    partsLineItems: 3,
    partsDollars: 150,
    apparelLineItems: 3,
    apparelDollars: 100,
    partsToLaborRatio: 125,
    serviceHoursPerRO: 3
  };

  // Calculate activity metrics based on average monthly unit sales and the
  // funnel ratios (greets:sit-downs:test-rides:write-ups per unit sold).
  const calculateActivityMetrics = (monthlyTargets, ratios = DEFAULT_ACTIVITY_RATIOS) => {
    if (!monthlyTargets || monthlyTargets.length === 0) {
      return { greets: 0, sitDowns: 0, testRides: 0, writeUps: 0 };
    }

    // Calculate average monthly total units (new + used)
    const totalUnits = monthlyTargets.reduce((sum, month) => {
      return sum + (month.newUnits || 0) + (month.usedUnits || 0);
    }, 0);
    const avgMonthlyUnits = totalUnits / 12;

    return {
      greets: Math.round(avgMonthlyUnits * (ratios.greets ?? DEFAULT_ACTIVITY_RATIOS.greets)),
      sitDowns: Math.round(avgMonthlyUnits * (ratios.sitDowns ?? DEFAULT_ACTIVITY_RATIOS.sitDowns)),
      testRides: Math.round(avgMonthlyUnits * (ratios.testRides ?? DEFAULT_ACTIVITY_RATIOS.testRides)),
      writeUps: Math.round(avgMonthlyUnits * (ratios.writeUps ?? DEFAULT_ACTIVITY_RATIOS.writeUps))
    };
  };

  // Recompute activity count targets when a funnel ratio changes.
  const handleActivityRatioChange = (field, value) => {
    const num = parseFloat(value) || 0;
    const setters = {
      greets: setGreetsRatio,
      sitDowns: setSitDownsRatio,
      testRides: setTestRidesRatio,
      writeUps: setWriteUpsRatio
    };
    setters[field](num);

    const ratios = {
      greets: field === 'greets' ? num : greetsRatio,
      sitDowns: field === 'sitDowns' ? num : sitDownsRatio,
      testRides: field === 'testRides' ? num : testRidesRatio,
      writeUps: field === 'writeUps' ? num : writeUpsRatio
    };

    setFrontEndTargets(prev => ({
      ...prev,
      ...calculateActivityMetrics(prev.monthlyTargets, ratios),
      greetsRatio: ratios.greets,
      sitDownsRatio: ratios.sitDowns,
      testRidesRatio: ratios.testRides,
      writeUpsRatio: ratios.writeUps
    }));
  };

  // Helper to select all text in input on focus
  const handleInputFocus = (e) => {
    // Use setTimeout to ensure selection happens after React finishes rendering
    setTimeout(() => {
      e.target.select();
    }, 0);
  };

  // Get ordered months based on fiscal year settings
  const getOrderedMonths = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const startMonth = dealershipSettings.fiscalYearStartMonth || 1;
    const orderedMonths = [];

    for (let i = 0; i < 12; i++) {
      const monthIndex = ((startMonth - 1 + i) % 12);
      orderedMonths.push({
        name: monthNames[monthIndex],
        index: monthIndex + 1 // 1-based index
      });
    }

    return orderedMonths;
  };

  // Fetch dealerships on mount
  useEffect(() => {
    fetchDealerships();
  }, [managedOwner]);

  // Fetch targets when dealership is selected
  useEffect(() => {
    if (selectedDealership) {
      fetchTargets();
      fetchEmployees();
      syncDealershipSettings(selectedDealership);
    }
  }, [selectedDealership, dealerships]);

  // Validate allocations after targets and employees load
  useEffect(() => {
    if (frontEndTargets && employees.length > 0) {
      const totalAllocation = calculateTotalAllocation();
      if (totalAllocation > 0 && totalAllocation !== 100) {
        console.warn(`⚠️ Allocation Warning: Total is ${totalAllocation}%, but should be 100%. Please review and adjust allocations.`);
      }
    }
  }, [frontEndTargets, employees]);

  // Clean up orphaned allocations when employees change
  useEffect(() => {
    if (frontEndTargets && frontEndTargets.employeeAllocations && employees.length > 0) {
      const validEmployeeIds = employees
        .filter(emp => {
          const roles = Array.isArray(emp.roles) ? emp.roles : [];
          return roles.includes('FIT_SPECIALIST') || roles.includes('Fit Specialist');
        })
        .map(emp => emp.id);

      const cleanedAllocations = {};
      Object.keys(frontEndTargets.employeeAllocations).forEach(employeeId => {
        // Keep allocation only if employee still exists and has Fit Specialist role
        if (validEmployeeIds.includes(employeeId)) {
          cleanedAllocations[employeeId] = frontEndTargets.employeeAllocations[employeeId];
        }
      });

      // If allocations changed, update state
      if (Object.keys(cleanedAllocations).length !== Object.keys(frontEndTargets.employeeAllocations).length) {
        setFrontEndTargets(prev => ({
          ...prev,
          employeeAllocations: cleanedAllocations
        }));
        console.log(`Cleaned up orphaned allocations. Removed ${Object.keys(frontEndTargets.employeeAllocations).length - Object.keys(cleanedAllocations).length} orphaned entries.`);
      }
    }
  }, [employees]);

  const fetchDealerships = async () => {
    try {
      let url = `${API_URL}/api/owners/dealerships`;
      if (managedOwner) {
        url += `?ownerId=${managedOwner.id}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch dealerships');
      const data = await response.json();
      setDealerships(data.dealerships || []);
      
      if (data.dealerships && data.dealerships.length > 0) {
        setSelectedDealership(data.dealerships[0].id);
        const first = data.dealerships[0];
        const settings = {
          performanceYearMode: first.performanceYearMode || 'CALENDAR',
          fiscalYearStartMonth: first.fiscalYearStartMonth || 1,
          fiscalYearStartDay: first.fiscalYearStartDay || 1,
          demoMode: first.demoMode || false
        };
        setDealershipSettings(settings);
        setOriginalSettings(settings);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const syncDealershipSettings = (dealershipId) => {
    const found = dealerships.find(d => d.id === dealershipId);
    if (found) {
      const settings = {
        performanceYearMode: found.performanceYearMode || 'CALENDAR',
        fiscalYearStartMonth: found.fiscalYearStartMonth || 1,
        fiscalYearStartDay: found.fiscalYearStartDay || 1,
        demoMode: found.demoMode || false
      };
      setDealershipSettings(settings);
      setOriginalSettings(settings);
    }
  };

  const saveDealershipSettings = async () => {
    try {
      const payload = {
        performanceYearMode: dealershipSettings.performanceYearMode,
        fiscalYearStartMonth: Number(dealershipSettings.fiscalYearStartMonth) || 1,
        fiscalYearStartDay: Number(dealershipSettings.fiscalYearStartDay) || 1,
        demoMode: dealershipSettings.demoMode
      };

      console.log('📝 Saving dealership settings:', { selectedDealership, payload });

      const response = await fetch(`${API_URL}/api/owners/dealerships/${selectedDealership}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('📤 PUT response status:', response.status);

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save dealership settings');
      }

      const responseData = await response.json();
      console.log('✅ PUT response data:', responseData);

      // Refetch dealerships from the owner endpoint to ensure we have the latest data
      try {
        const dealershipsResponse = await fetch(`${API_URL}/api/owners/dealerships`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (dealershipsResponse.ok) {
          const data = await dealershipsResponse.json();
          console.log('📥 Refetched dealerships:', data.dealerships);
          setDealerships(data.dealerships || []);
          
          // Also immediately sync the current dealership settings with the refetched data
          const updatedDealership = (data.dealerships || []).find(d => d.id === selectedDealership);
          if (updatedDealership) {
            const updatedSettings = {
              performanceYearMode: updatedDealership.performanceYearMode || 'CALENDAR',
              fiscalYearStartMonth: updatedDealership.fiscalYearStartMonth || 1,
              fiscalYearStartDay: updatedDealership.fiscalYearStartDay || 1,
              demoMode: updatedDealership.demoMode || false
            };
            console.log('🔄 Synced settings from API:', updatedSettings);
            setDealershipSettings(updatedSettings);
            setOriginalSettings(updatedSettings);
          }
        }
      } catch (err) {
        console.error('Failed to refetch dealerships:', err);
        // If refetch fails, at least update the local state
        setDealerships(prev => prev.map(d => d.id === selectedDealership ? { ...d, ...payload } : d));
        setOriginalSettings(dealershipSettings);
      }

      setSaveMessage('Fiscal settings saved');
      setTimeout(() => setSaveMessage(null), 2500);
    } catch (err) {
      console.error('❌ Error saving dealership settings:', err);
      setError(err.message);
    }
  };

  const fetchTargets = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/targets/${selectedDealership}`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const frontEndData = data.frontEnd || {};
        const fixedOpsData = data.fixedOps || {};

        // Initialize monthly targets for Fixed Ops (Parts, Service, Apparel)
        const fixedOpsMonthlyTargets = fixedOpsData.monthlyTargets || Array.from({ length: 12 }, (_, i) => ({
          month: i + 1,
          // Parts
          partsNetSales: 0,
          partsGrossProfit: 0,
          partsGrossMargin: 0,
          partsTransactions: 0,
          partsItems: 0,
          partsInventoryValue: 0,
          // Service
          serviceNetSales: 0,
          serviceGrossProfit: 0,
          serviceGrossMargin: 0,
          serviceTransactions: 0,
          serviceHours: 0,
          serviceOpenROsInternal: 0,
          serviceOpenROsCustomer: 0,
          // Apparel & Licensing
          apparelNetSales: 0,
          apparelGrossProfit: 0,
          apparelGrossMargin: 0,
          apparelTransactions: 0,
          apparelItems: 0,
          apparelInventoryValue: 0
        }));

        const normalizedFixedOps = {
          ...fixedOpsData,
          monthlyTargets: fixedOpsMonthlyTargets,
          serviceTechnicians: fixedOpsData.serviceTechnicians || 0,
          laborRate: fixedOpsData.laborRate || 150,
          dealAddOnTargets: {
            parts: fixedOpsData.dealAddOnTargets?.parts ?? 600,
            labor: fixedOpsData.dealAddOnTargets?.labor ?? 300,
            apparel: fixedOpsData.dealAddOnTargets?.apparel ?? 100
          },
          inventoryTurns: {
            partsMin: fixedOpsData.inventoryTurns?.partsMin ?? 3,
            partsMax: fixedOpsData.inventoryTurns?.partsMax ?? 5,
            apparelMin: fixedOpsData.inventoryTurns?.apparelMin ?? 2,
            apparelMax: fixedOpsData.inventoryTurns?.apparelMax ?? 4
          },
          benchmarks: {
            partsLineItems: fixedOpsData.benchmarks?.partsLineItems ?? DEFAULT_BENCHMARKS.partsLineItems,
            partsDollars: fixedOpsData.benchmarks?.partsDollars ?? DEFAULT_BENCHMARKS.partsDollars,
            apparelLineItems: fixedOpsData.benchmarks?.apparelLineItems ?? DEFAULT_BENCHMARKS.apparelLineItems,
            apparelDollars: fixedOpsData.benchmarks?.apparelDollars ?? DEFAULT_BENCHMARKS.apparelDollars,
            partsToLaborRatio: fixedOpsData.benchmarks?.partsToLaborRatio ?? DEFAULT_BENCHMARKS.partsToLaborRatio,
            serviceHoursPerRO: fixedOpsData.benchmarks?.serviceHoursPerRO ?? DEFAULT_BENCHMARKS.serviceHoursPerRO
          }
        };

        // Initialize monthly targets array if not present
        const monthlyTargets = frontEndData.monthlyTargets || Array.from({ length: 12 }, (_, i) => ({
          month: i + 1,
          newUnits: 0,
          usedUnits: 0,
          newGrossProfit: 0,
          usedGrossProfit: 0,
          backEndGrossProfit: 0,
          fiGrossProfit: 0,
          transactions: 0
        }));

        // Ensure missing fields are hydrated with defaults and migrate old data if present
        const migratedMonthlyTargets = monthlyTargets.map(target => ({
          month: target.month,
          newUnits: target.newUnits ?? target.unitSales ?? 0,
          usedUnits: target.usedUnits ?? 0,
          newGrossProfit: target.newGrossProfit ?? target.grossProfit ?? 0,
          usedGrossProfit: target.usedGrossProfit ?? 0,
          backEndGrossProfit: target.backEndGrossProfit ?? 0,
          fiGrossProfit: target.fiGrossProfit ?? target.fiIncome ?? 0,
          transactions: target.transactions ?? ((target.newUnits ?? target.unitSales ?? 0) + (target.usedUnits ?? 0)) * 20
        }));

        // Set front end targets with migrated monthly data and calculated activity metrics
        const savedTransactionsRatio = frontEndData.transactionsRatio || 20;
        const savedTransactionsToSitDownsRatio = frontEndData.transactionsToSitDownsRatio || 5;
        const savedTransactionsToGreetsRatio = frontEndData.transactionsToGreetsRatio || 2;
        setTransactionsRatio(savedTransactionsRatio);
        setTransactionsToSitDownsRatio(savedTransactionsToSitDownsRatio);
        setTransactionsToGreetsRatio(savedTransactionsToGreetsRatio);

        // Load saved funnel ratios (fall back to the standard 10:4:3:2:1)
        const savedActivityRatios = {
          greets: frontEndData.greetsRatio || DEFAULT_ACTIVITY_RATIOS.greets,
          sitDowns: frontEndData.sitDownsRatio || DEFAULT_ACTIVITY_RATIOS.sitDowns,
          testRides: frontEndData.testRidesRatio || DEFAULT_ACTIVITY_RATIOS.testRides,
          writeUps: frontEndData.writeUpsRatio || DEFAULT_ACTIVITY_RATIOS.writeUps
        };
        setGreetsRatio(savedActivityRatios.greets);
        setSitDownsRatio(savedActivityRatios.sitDowns);
        setTestRidesRatio(savedActivityRatios.testRides);
        setWriteUpsRatio(savedActivityRatios.writeUps);

        // Activity count targets are always derived from projected sales × ratio
        const activityMetrics = calculateActivityMetrics(migratedMonthlyTargets, savedActivityRatios);

        setFrontEndTargets({
          ...frontEndData,
          monthlyTargets: migratedMonthlyTargets,
          greets: activityMetrics.greets,
          sitDowns: activityMetrics.sitDowns,
          testRides: activityMetrics.testRides,
          writeUps: activityMetrics.writeUps,
          transactionsRatio: savedTransactionsRatio,
          transactionsToSitDownsRatio: savedTransactionsToSitDownsRatio,
          transactionsToGreetsRatio: savedTransactionsToGreetsRatio,
          greetsRatio: savedActivityRatios.greets,
          sitDownsRatio: savedActivityRatios.sitDowns,
          testRidesRatio: savedActivityRatios.testRides,
          writeUpsRatio: savedActivityRatios.writeUps,
          metricsFrequency: frontEndData.metricsFrequency || 'monthly',
          docFee: normalizeDocFee(frontEndData.docFee),
          fiProducts: normalizeFiProducts(frontEndData.fiProducts),
          employeeAllocations: frontEndData.employeeAllocations || {}
        });

        const savedDocFee = normalizeDocFee(frontEndData.docFee);
        setDocFeeInputs({
          new: formatCurrencyCents(savedDocFee.new.amount),
          used: formatCurrencyCents(savedDocFee.used.amount)
        });

        setFixedOpsTargets(normalizedFixedOps);

        // Sync UI frequency selectors with saved values, default to monthly
        if (frontEndData.metricsFrequency) {
          setMetricsFrequency(frontEndData.metricsFrequency);
        } else {
          setMetricsFrequency('monthly');
        }
      } else {
        // Initialize empty targets if none exist
        const emptyMonthlyTargets = Array.from({ length: 12 }, (_, i) => ({
          month: i + 1,
          newUnits: 0,
          usedUnits: 0,
          newGrossProfit: 0,
          usedGrossProfit: 0,
          backEndGrossProfit: 0,
          fiGrossProfit: 0,
          transactions: 0
        }));

        const activityMetrics = calculateActivityMetrics(emptyMonthlyTargets, DEFAULT_ACTIVITY_RATIOS);
        setTransactionsRatio(20);
        setTransactionsToSitDownsRatio(5);
        setTransactionsToGreetsRatio(2);
        setGreetsRatio(DEFAULT_ACTIVITY_RATIOS.greets);
        setSitDownsRatio(DEFAULT_ACTIVITY_RATIOS.sitDowns);
        setTestRidesRatio(DEFAULT_ACTIVITY_RATIOS.testRides);
        setWriteUpsRatio(DEFAULT_ACTIVITY_RATIOS.writeUps);

        setFrontEndTargets({
          monthlyTargets: emptyMonthlyTargets,
          greets: activityMetrics.greets,
          sitDowns: activityMetrics.sitDowns,
          testRides: activityMetrics.testRides,
          writeUps: activityMetrics.writeUps,
          transactionsRatio: 20,
          transactionsToSitDownsRatio: 5,
          transactionsToGreetsRatio: 2,
          greetsRatio: DEFAULT_ACTIVITY_RATIOS.greets,
          sitDownsRatio: DEFAULT_ACTIVITY_RATIOS.sitDowns,
          testRidesRatio: DEFAULT_ACTIVITY_RATIOS.testRides,
          writeUpsRatio: DEFAULT_ACTIVITY_RATIOS.writeUps,
          metricsFrequency: 'monthly',
          docFee: normalizeDocFee(null),
          fiProducts: normalizeFiProducts(null),
          employeeAllocations: {}
        });
        setDocFeeInputs({ new: formatCurrencyCents(0), used: formatCurrencyCents(0) });
        const emptyFixedOpsMonthlyTargets = Array.from({ length: 12 }, (_, i) => ({
          month: i + 1,
          // Parts
          partsNetSales: 0,
          partsGrossProfit: 0,
          partsGrossMargin: 0,
          partsTransactions: 0,
          partsItems: 0,
          partsInventoryValue: 0,
          // Service
          serviceNetSales: 0,
          serviceGrossProfit: 0,
          serviceGrossMargin: 0,
          serviceTransactions: 0,
          serviceHours: 0,
          serviceOpenROsInternal: 0,
          serviceOpenROsCustomer: 0,
          // Apparel & Licensing
          apparelNetSales: 0,
          apparelGrossProfit: 0,
          apparelGrossMargin: 0,
          apparelTransactions: 0,
          apparelItems: 0,
          apparelInventoryValue: 0
        }));

        setFixedOpsTargets({
          monthlyTargets: emptyFixedOpsMonthlyTargets,
          serviceTechnicians: 0,
          laborRate: 0,
          dealAddOnTargets: {
            parts: 600,
            labor: 300,
            apparel: 100
          },
          inventoryTurns: {
            partsMin: 3,
            partsMax: 5,
            apparelMin: 2,
            apparelMax: 4
          },
          benchmarks: { ...DEFAULT_BENCHMARKS }
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/dealerships/${selectedDealership}/employees`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const handleFrontEndTargetChange = (field, value) => {
    setFrontEndTargets(prev => ({
      ...prev,
      [field]: value === '' ? '' : (parseFloat(value) || 0)
    }));
  };

  const handleFrontEndTargetBlur = (field) => {
    setFrontEndTargets(prev => ({
      ...prev,
      [field]: prev[field] === '' ? 0 : (parseFloat(prev[field]) || 0)
    }));
  };

  const handleMonthlyTargetChange = (monthIndex, field, value) => {
    setFrontEndTargets(prev => {
      const monthlyTargets = [...prev.monthlyTargets];
      const updatedMonth = {
        ...monthlyTargets[monthIndex],
        [field]: value === '' ? '' : (parseFloat(value) || 0)
      };

      // Auto-calculate transactions when units change (unless user is editing transactions directly)
      if (field === 'newUnits' || field === 'usedUnits') {
        const newUnits = field === 'newUnits' ? (parseFloat(value) || 0) : (updatedMonth.newUnits || 0);
        const usedUnits = field === 'usedUnits' ? (parseFloat(value) || 0) : (updatedMonth.usedUnits || 0);
        updatedMonth.transactions = (newUnits + usedUnits) * transactionsRatio;
      }

      monthlyTargets[monthIndex] = updatedMonth;

      // Recalculate activity metrics when units change
      const activityMetrics = calculateActivityMetrics(monthlyTargets);

      return {
        ...prev,
        monthlyTargets,
        ...activityMetrics
      };
    });
  };

  const handleMonthlyTargetBlur = (monthIndex, field) => {
    setFrontEndTargets(prev => {
      const monthlyTargets = [...prev.monthlyTargets];
      const currentValue = monthlyTargets[monthIndex][field];
      monthlyTargets[monthIndex] = {
        ...monthlyTargets[monthIndex],
        [field]: currentValue === '' ? 0 : (parseFloat(currentValue) || 0)
      };
      return {
        ...prev,
        monthlyTargets
      };
    });
  };

  const handleEmployeeAllocationChange = (employeeId, percentage) => {
    setFrontEndTargets(prev => ({
      ...prev,
      employeeAllocations: {
        ...prev.employeeAllocations,
        [employeeId]: percentage === '' ? '' : (Math.round(parseFloat(percentage)) || 0)
      }
    }));
  };

  const handleEmployeeAllocationBlur = (employeeId) => {
    setFrontEndTargets(prev => ({
      ...prev,
      employeeAllocations: {
        ...prev.employeeAllocations,
        [employeeId]: prev.employeeAllocations?.[employeeId] === '' ? 0 : (Math.round(parseFloat(prev.employeeAllocations?.[employeeId])) || 0)
      }
    }));
  };

  const handleFixedOpsMonthlyTargetChange = (monthIndex, field, value) => {
    setFixedOpsTargets(prev => {
      const monthlyTargets = [...prev.monthlyTargets];
      const updatedMonth = {
        ...monthlyTargets[monthIndex],
        [field]: value === '' ? '' : (parseFloat(value) || 0)
      };

      // Auto-calculate items for Parts (3:1 ratio - 3 items per transaction)
      if (field === 'partsTransactions') {
        updatedMonth.partsItems = Math.round((parseFloat(value) || 0) * 3);
      }

      // Auto-calculate items for Apparel (3:1 ratio - 3 items per transaction)
      if (field === 'apparelTransactions') {
        updatedMonth.apparelItems = Math.round((parseFloat(value) || 0) * 3);
      }

      monthlyTargets[monthIndex] = updatedMonth;

      return {
        ...prev,
        monthlyTargets
      };
    });
  };

  const handleFixedOpsMonthlyTargetBlur = (monthIndex, field) => {
    setFixedOpsTargets(prev => {
      const monthlyTargets = [...prev.monthlyTargets];
      const currentValue = monthlyTargets[monthIndex][field];
      monthlyTargets[monthIndex] = {
        ...monthlyTargets[monthIndex],
        [field]: currentValue === '' ? 0 : (parseFloat(currentValue) || 0)
      };
      return {
        ...prev,
        monthlyTargets
      };
    });
  };

  const handleMetricsFrequencyChange = (frequency) => {
    setMetricsFrequency(frequency);
    setFrontEndTargets(prev => ({
      ...prev,
      metricsFrequency: frequency
    }));
  };

  // Doc fee lives on the front-end targets blob, so saveFrontEndTargets picks it
  // up with the rest of the spread. `kind` is 'new' or 'used' — the two tiers are
  // independent, so editing one leaves the other exactly as it was. Keep the
  // amount even when a tier is switched off so toggling it back on doesn't lose
  // what was typed.
  const handleDocFeeChange = (kind, field, value) => {
    setFrontEndTargets(prev => {
      const current = normalizeDocFee(prev?.docFee);
      return {
        ...prev,
        docFee: {
          ...current,
          [kind]: {
            enabled: field === 'enabled' ? value : current[kind].enabled,
            amount: field === 'amount' ? (parseFloat(value) || 0) : current[kind].amount
          }
        }
      };
    });
  };

  // Accept digits and a single decimal point while typing; the field reformats
  // to dollars and cents on blur.
  const handleDocFeeAmountChange = (kind, raw) => {
    const cleaned = raw.replace(/[^\d.]/g, '').replace(/(\.[^.]*)\./g, '$1');
    setDocFeeInputs(prev => ({ ...prev, [kind]: cleaned }));
    handleDocFeeChange(kind, 'amount', cleaned);
  };

  const handleDocFeeAmountFocus = (kind, e) => {
    const num = parseFloat(String(docFeeInputs[kind]).replace(/[^\d.]/g, '')) || 0;
    setDocFeeInputs(prev => ({ ...prev, [kind]: num ? String(num) : '' }));
    handleInputFocus(e);
  };

  const handleDocFeeAmountBlur = (kind) => {
    const num = parseFloat(String(docFeeInputs[kind]).replace(/[^\d.]/g, '')) || 0;
    handleDocFeeChange(kind, 'amount', num);
    setDocFeeInputs(prev => ({ ...prev, [kind]: formatCurrencyCents(num) }));
  };

  // F&I product benchmarks — the penetration and average bars the F&I
  // Scoreboard's product cards measure against. Two independent numbers per
  // product, so an edit to one leaves the other and the other five products
  // exactly as they were. Blank is kept while typing and resolved on blur.
  const handleFiProductChange = (field, key, value) => {
    setFrontEndTargets(prev => {
      const current = normalizeFiProducts(prev?.fiProducts);
      return {
        ...prev,
        fiProducts: {
          ...current,
          [field]: {
            ...current[field],
            [key]: value === '' ? '' : (parseFloat(value) || 0)
          }
        }
      };
    });
  };

  // A blank or zero bar would peg the card at 0% forever, so an emptied field
  // falls back to the industry default rather than saving a benchmark that
  // paints the product permanently red. Same rule the Fixed Ops benchmarks use.
  const handleFiProductBlur = (field, key) => {
    setFrontEndTargets(prev => {
      const current = normalizeFiProducts(prev?.fiProducts);
      const entered = parseFloat(prev?.fiProducts?.[field]?.[key]);
      const fallback = FI_PRODUCT_DEFAULTS.find(p => p.field === field)[key];
      return {
        ...prev,
        fiProducts: {
          ...current,
          [field]: {
            ...current[field],
            [key]: entered > 0 ? entered : fallback
          }
        }
      };
    });
  };

  const handleTransactionsRatioChange = (value) => {
    const ratio = parseFloat(value) || 0;
    setTransactionsRatio(ratio);

    // Recalculate all monthly transactions based on new ratio
    setFrontEndTargets(prev => {
      const updatedMonthlyTargets = prev.monthlyTargets.map(month => ({
        ...month,
        transactions: ((month.newUnits || 0) + (month.usedUnits || 0)) * ratio
      }));

      return {
        ...prev,
        monthlyTargets: updatedMonthlyTargets,
        transactionsRatio: ratio
      };
    });
  };

  const handleServiceTechniciansChange = (value) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      serviceTechnicians: value === '' ? '' : (parseInt(value) || 0)
    }));
  };

  const handleServiceTechniciansBlur = () => {
    setFixedOpsTargets(prev => ({
      ...prev,
      serviceTechnicians: prev.serviceTechnicians === '' ? 0 : (parseInt(prev.serviceTechnicians) || 0)
    }));
  };

  const handleLaborRateChange = (value) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      laborRate: value === '' ? '' : (parseFloat(value) || 0)
    }));
  };

  const handleLaborRateBlur = () => {
    setFixedOpsTargets(prev => ({
      ...prev,
      laborRate: prev.laborRate === '' ? 0 : (parseFloat(prev.laborRate) || 0)
    }));
  };

  const handleDealAddOnChange = (field, value) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      dealAddOnTargets: {
        ...prev.dealAddOnTargets,
        [field]: value === '' ? '' : (parseFloat(value) || 0)
      }
    }));
  };

  const handleDealAddOnBlur = (field) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      dealAddOnTargets: {
        ...prev.dealAddOnTargets,
        [field]: prev.dealAddOnTargets[field] === '' ? 0 : (parseFloat(prev.dealAddOnTargets[field]) || 0)
      }
    }));
  };

  const handleInventoryTurnsChange = (field, value) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      inventoryTurns: {
        ...prev.inventoryTurns,
        [field]: value === '' ? '' : (parseFloat(value) || 0)
      }
    }));
  };

  const handleInventoryTurnsBlur = (field) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      inventoryTurns: {
        ...prev.inventoryTurns,
        [field]: prev.inventoryTurns[field] === '' ? 0 : (parseFloat(prev.inventoryTurns[field]) || 0)
      }
    }));
  };

  const handleBenchmarkChange = (field, value) => {
    setFixedOpsTargets(prev => ({
      ...prev,
      benchmarks: {
        ...prev.benchmarks,
        [field]: value === '' ? '' : (parseFloat(value) || 0)
      }
    }));
  };

  // A blank or zero benchmark has no meaning on the Profit Summary, so an
  // emptied field falls back to the industry default instead of 0.
  const handleBenchmarkBlur = (field) => {
    setFixedOpsTargets(prev => {
      const entered = parseFloat(prev.benchmarks?.[field]);
      return {
        ...prev,
        benchmarks: {
          ...prev.benchmarks,
          [field]: entered > 0 ? entered : DEFAULT_BENCHMARKS[field]
        }
      };
    });
  };

  const calculateTotalAllocation = () => {
    if (!frontEndTargets || !frontEndTargets.employeeAllocations) return 0;
    return Object.values(frontEndTargets.employeeAllocations).reduce((sum, pct) => sum + pct, 0);
  };

  const distributeEvenly = () => {
    const fitSpecialists = employees.filter(emp => {
      const roles = Array.isArray(emp.roles) ? emp.roles : [];
      return roles.includes('FIT_SPECIALIST') || roles.includes('Fit Specialist');
    });
    if (fitSpecialists.length === 0) return;
    
    const baseAllocation = Math.floor(100 / fitSpecialists.length);
    const remainder = 100 % fitSpecialists.length;
    const newAllocations = {};
    
    fitSpecialists.forEach((employee, index) => {
      // Distribute the remainder across the first few employees
      newAllocations[employee.id] = baseAllocation + (index < remainder ? 1 : 0);
    });
    setFrontEndTargets(prev => ({
      ...prev,
      employeeAllocations: newAllocations
    }));
  };

  const performSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/targets/${selectedDealership}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          frontEnd: {
            ...frontEndTargets,
            transactionsRatio,
            transactionsToSitDownsRatio,
            transactionsToGreetsRatio,
            greetsRatio,
            sitDownsRatio,
            testRidesRatio,
            writeUpsRatio,
            metricsFrequency
          },
          dealershipId: selectedDealership
        })
      });

      if (response.ok) {
        setSaveMessage('Sales targets saved successfully!');
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      setError('Failed to save targets: ' + err.message);
    }
  };

  const saveFrontEndTargets = async () => {
    // Validate monthly targets for zero values
    let hasZeroValues = false;
    const zeroFields = [];

    if (frontEndTargets.monthlyTargets) {
      frontEndTargets.monthlyTargets.forEach((monthTarget) => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[monthTarget.month - 1];

        if (!monthTarget.newUnits && !monthTarget.usedUnits) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: New Units and Used Units`);
        }
        if (!monthTarget.newGrossProfit && monthTarget.newUnits > 0) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: New Gross Profit`);
        }
        if (!monthTarget.usedGrossProfit && monthTarget.usedUnits > 0) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Used Gross Profit`);
        }
        if (!monthTarget.backEndGrossProfit) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: F&I Reserve GP`);
        }
        if (!monthTarget.fiGrossProfit) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: F&I Product GP`);
        }
        if (!monthTarget.transactions) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: # Transactions`);
        }
      });
    }

    // Show warning if there are zero values
    if (hasZeroValues) {
      const message = `The following fields have zero values:\n\n${zeroFields.slice(0, 10).join('\n')}${zeroFields.length > 10 ? `\n...and ${zeroFields.length - 10} more` : ''}`;
      setWarningMessage(message);
      setShowWarningModal(true);
      setPendingSave(() => performSave);
    } else {
      await performSave();
    }
  };

  const handleConfirmSave = async () => {
    if (pendingSave) {
      await pendingSave();
      setPendingSave(null);
    }
    setShowWarningModal(false);
  };

  const performFixedOpsSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/targets/${selectedDealership}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fixedOps: fixedOpsTargets,
          dealershipId: selectedDealership
        })
      });

      if (response.ok) {
        setSaveMessage('Fixed Operations targets saved successfully!');
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      setError('Failed to save targets: ' + err.message);
    }
  };

  const saveFixedOpsTargets = async () => {
    // Validate monthly targets for zero values
    let hasZeroValues = false;
    const zeroFields = [];

    if (fixedOpsTargets.monthlyTargets) {
      fixedOpsTargets.monthlyTargets.forEach((monthTarget) => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[monthTarget.month - 1];

        // Parts validation
        if (!monthTarget.partsNetSales && !monthTarget.partsGrossProfit) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Parts Net Sales and Gross Profit`);
        }
        if (!monthTarget.partsTransactions) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Parts Transactions`);
        }

        // Service validation
        if (!monthTarget.serviceNetSales && !monthTarget.serviceGrossProfit) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Service Net Sales and Gross Profit`);
        }
        if (!monthTarget.serviceTransactions) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Service Transactions`);
        }

        // Apparel validation
        if (!monthTarget.apparelNetSales && !monthTarget.apparelGrossProfit) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Apparel Net Sales and Gross Profit`);
        }
        if (!monthTarget.apparelTransactions) {
          hasZeroValues = true;
          zeroFields.push(`${monthName}: Apparel Transactions`);
        }
      });
    }

    // Show warning if there are zero values
    if (hasZeroValues) {
      const message = `The following fields have zero values:\n\n${zeroFields.slice(0, 10).join('\n')}${zeroFields.length > 10 ? `\n...and ${zeroFields.length - 10} more` : ''}`;
      setWarningMessage(message);
      setShowWarningModal(true);
      setPendingSave(() => performFixedOpsSave);
    } else {
      await performFixedOpsSave();
    }
  };

  if (loading) {
    return <div className="targets-loading">Loading targets...</div>;
  }

  if (error) {
    return <div className="targets-error">Error: {error}</div>;
  }

  const totalAllocation = calculateTotalAllocation();
  const allocationWarning = totalAllocation !== 100 && totalAllocation > 0;
  const settingsChanged = JSON.stringify(dealershipSettings) !== JSON.stringify(originalSettings);

  return (
    <div className="targets-container">
      <div className="targets-header">
        <div>
          <h1>Targets</h1>
          <p>Set sales and fixed operations targets for your dealerships</p>
        </div>
      </div>

      {/* Dealership Selector */}
      <div className="targets-controls">
        <div className="dealership-selector">
          <label>Select Dealership:</label>
          <select 
            value={selectedDealership || ''} 
            onChange={(e) => setSelectedDealership(e.target.value)}
            className="dealership-select"
          >
            {dealerships.map(d => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="fiscal-settings">
          <label>Performance Year</label>
          <select
            value={dealershipSettings.performanceYearMode}
            onChange={(e) => setDealershipSettings(prev => ({ ...prev, performanceYearMode: e.target.value }))}
            className="fiscal-select"
          >
            <option value="CALENDAR">Calendar (Jan 1 - Dec 31)</option>
            <option value="FISCAL">Fiscal (custom start)</option>
          </select>
        </div>

        {dealershipSettings.performanceYearMode === 'FISCAL' && (
          <>
            <div className="fiscal-settings">
              <label>Start Month</label>
              <input
                type="number"
                min="1"
                max="12"
                value={dealershipSettings.fiscalYearStartMonth}
                onChange={(e) => setDealershipSettings(prev => ({ ...prev, fiscalYearStartMonth: e.target.value }))}
                className="fiscal-input-field"
              />
            </div>
            <div className="fiscal-settings">
              <label>Start Day</label>
              <input
                type="number"
                min="1"
                max="28"
                value={dealershipSettings.fiscalYearStartDay}
                onChange={(e) => setDealershipSettings(prev => ({ ...prev, fiscalYearStartDay: e.target.value }))}
                className="fiscal-input-field"
              />
            </div>
          </>
        )}

        

        {settingsChanged && (
          <button className="btn-save-fiscal" onClick={saveDealershipSettings}>Save Settings</button>
        )}
      </div>

      {saveMessage && (
        <div className="success-message">{saveMessage}</div>
      )}

      {/* Tab Navigation */}
      <div className="targets-tabs">
        <button 
          className={`tab-button ${activeTab === 'frontend' ? 'active' : ''}`}
          onClick={() => setActiveTab('frontend')}
        >
          Sales
        </button>
        <button 
          className={`tab-button ${activeTab === 'fixedops' ? 'active' : ''}`}
          onClick={() => setActiveTab('fixedops')}
        >
          Fixed Operations
        </button>
      </div>

      {/* Sales Targets Tab */}
      {activeTab === 'frontend' && frontEndTargets && (
        <div className="tab-content">
          <div className="targets-section">
            <div className="section-header">
              <h2>Sales Targets</h2>
            </div>

            {/* Monthly Targets Table */}
            <div className="targets-group">
              <div className="group-header">
                <h3 className="group-title">Monthly Sales Targets</h3>
                <button className="btn-save-targets" onClick={saveFrontEndTargets}>
                  Save Monthly Targets
                </button>
              </div>
              <div className="monthly-targets-table">
                <div className="monthly-table-header">
                  <div className="monthly-header-cell">Month</div>
                  <div className="monthly-header-cell">New Units</div>
                  <div className="monthly-header-cell">Used Units</div>
                  <div className="monthly-header-cell">New Gross Profit</div>
                  <div className="monthly-header-cell">Used Gross Profit</div>
                  <div className="monthly-header-cell">F&I Reserve GP</div>
                  <div className="monthly-header-cell">F&I Product GP</div>
                  <div className="monthly-header-cell"># Transactions</div>
                </div>
                {frontEndTargets.monthlyTargets && getOrderedMonths().map((monthInfo, index) => {
                  const monthData = frontEndTargets.monthlyTargets.find(m => m.month === monthInfo.index) || {
                    month: monthInfo.index,
                    newUnits: 0,
                    usedUnits: 0,
                    newGrossProfit: 0,
                    usedGrossProfit: 0,
                    backEndGrossProfit: 0,
                    fiGrossProfit: 0,
                    transactions: 0
                  };
                  const dataIndex = frontEndTargets.monthlyTargets.findIndex(m => m.month === monthInfo.index);

                  return (
                    <div key={monthInfo.index} className="monthly-table-row">
                      <div className="monthly-cell month-name">{monthInfo.name}</div>

                      {/* New Units */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          className={!monthData.newUnits && !monthData.usedUnits ? 'zero-warning' : ''}
                          value={monthData.newUnits || 0}
                          onChange={(e) => handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'newUnits', e.target.value)}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'newUnits')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Used Units */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          className={!monthData.newUnits && !monthData.usedUnits ? 'zero-warning' : ''}
                          value={monthData.usedUnits || 0}
                          onChange={(e) => handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'usedUnits', e.target.value)}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'usedUnits')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* New Gross Profit */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          className={!monthData.newGrossProfit && monthData.newUnits > 0 ? 'zero-warning' : ''}
                          value={monthData.newGrossProfit === 0 ? '0' : formatCurrency(monthData.newGrossProfit)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'newGrossProfit', numValue);
                          }}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'newGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Used Gross Profit */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          className={!monthData.usedGrossProfit && monthData.usedUnits > 0 ? 'zero-warning' : ''}
                          value={monthData.usedGrossProfit === 0 ? '0' : formatCurrency(monthData.usedGrossProfit)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'usedGrossProfit', numValue);
                          }}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'usedGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* F&I Reserve GP (stored as backEndGrossProfit) */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          className={!monthData.backEndGrossProfit ? 'zero-warning' : ''}
                          value={monthData.backEndGrossProfit === 0 ? '0' : formatCurrency(monthData.backEndGrossProfit)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'backEndGrossProfit', numValue);
                          }}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'backEndGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* F&I Product GP (stored as fiGrossProfit) */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          className={!monthData.fiGrossProfit ? 'zero-warning' : ''}
                          value={monthData.fiGrossProfit === 0 ? '0' : formatCurrency(monthData.fiGrossProfit)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'fiGrossProfit', numValue);
                          }}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'fiGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Transactions */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          className={!monthData.transactions ? 'zero-warning' : ''}
                          value={monthData.transactions || 0}
                          onChange={(e) => handleMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'transactions', e.target.value)}
                          onBlur={() => handleMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'transactions')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly Activity Metrics Group */}
            <div className="targets-group">
              <div className="group-header">
                <h3 className="group-title">Monthly Activity Metrics</h3>
                <button className="btn-save-targets" onClick={saveFrontEndTargets}>
                  Save Activity Metrics
                </button>
              </div>
              <p className="auto-calculated-note">
                The counts below are automatically calculated from your projected monthly sales using the funnel ratios
                ({greetsRatio}:{sitDownsRatio}:{testRidesRatio}:{writeUpsRatio}:1 — Greets:Sit-Downs:Test Rides:Write-Ups:Unit Sales).
                Edit the per-sale ratios to change how the counts are calculated.
              </p>
              <div className="targets-grid" style={{ marginBottom: '16px' }}>
                <div className="target-input-group">
                  <label>Greets per Sale Ratio</label>
                  <input
                    type="number"
                    value={greetsRatio}
                    onChange={(e) => handleActivityRatioChange('greets', e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="10"
                    min="0"
                  />
                </div>
                <div className="target-input-group">
                  <label>Sit-Downs per Sale Ratio</label>
                  <input
                    type="number"
                    value={sitDownsRatio}
                    onChange={(e) => handleActivityRatioChange('sitDowns', e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="4"
                    min="0"
                  />
                </div>
                <div className="target-input-group">
                  <label>Test Rides per Sale Ratio</label>
                  <input
                    type="number"
                    value={testRidesRatio}
                    onChange={(e) => handleActivityRatioChange('testRides', e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="3"
                    min="0"
                  />
                </div>
                <div className="target-input-group">
                  <label>Write-Ups per Sale Ratio</label>
                  <input
                    type="number"
                    value={writeUpsRatio}
                    onChange={(e) => handleActivityRatioChange('writeUps', e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="2"
                    min="0"
                  />
                </div>
              </div>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Greets</label>
                  <input
                    type="number"
                    value={frontEndTargets.greets || 0}
                    readOnly
                    placeholder="0"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="target-input-group">
                  <label>Sit-Downs</label>
                  <input
                    type="number"
                    value={frontEndTargets.sitDowns || 0}
                    readOnly
                    placeholder="0"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="target-input-group">
                  <label>Test Rides</label>
                  <input
                    type="number"
                    value={frontEndTargets.testRides || 0}
                    readOnly
                    placeholder="0"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="target-input-group">
                  <label>Write-Ups</label>
                  <input
                    type="number"
                    value={frontEndTargets.writeUps || 0}
                    readOnly
                    placeholder="0"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </div>

            {/* Transactions Group - Separate section */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Transactions</h3>
              </div>
              <p className="auto-calculated-note">
                Transactions are calculated based on total bikes sold (new + used) per month.
                Default ratio: {transactionsRatio}:1 (transactions per bike sold).
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Transactions per Bike Ratio</label>
                  <input
                    type="number"
                    value={transactionsRatio}
                    onChange={(e) => handleTransactionsRatioChange(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="20"
                    min="1"
                  />
                </div>
                <div className="target-input-group">
                  <label>Transactions per Sit-Down Ratio</label>
                  <input
                    type="number"
                    value={transactionsToSitDownsRatio}
                    onChange={(e) => setTransactionsToSitDownsRatio(parseFloat(e.target.value) || 0)}
                    onFocus={handleInputFocus}
                    placeholder="5"
                    min="1"
                  />
                </div>
                <div className="target-input-group">
                  <label>Transactions per Greet Ratio</label>
                  <input
                    type="number"
                    value={transactionsToGreetsRatio}
                    onChange={(e) => setTransactionsToGreetsRatio(parseFloat(e.target.value) || 0)}
                    onFocus={handleInputFocus}
                    placeholder="2"
                    min="1"
                  />
                </div>
                <div className="target-input-group">
                  <label>Average Monthly Transactions</label>
                  <input
                    type="number"
                    value={Math.round(frontEndTargets.monthlyTargets.reduce((sum, m) => sum + (m.transactions || 0), 0) / 12)}
                    readOnly
                    placeholder="0"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </div>

            {/* Doc Fee — flat per-bike fee, counted as back-end (F&I) gross.
                New and used are set independently: either, both, or neither. */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Doc Fee</h3>
                <button className="btn-save-targets" onClick={saveFrontEndTargets}>
                  Save Doc Fee
                </button>
              </div>
              <p className="auto-calculated-note">
                A flat fee added to every bike sold. New and used are set separately, so
                you can charge it on one, both, or neither, at different amounts. When
                enabled it counts toward back-end (F&amp;I) gross profit on reports—it
                isn't entered per deal and doesn't change unit gross. Applies to every
                bike already on record, so changing it restates past months. Choose your
                amounts, then use Save Doc Fee above.
              </p>
              <div className="targets-grid doc-fee-grid">
                <div className="target-input-group">
                  <label>Charge a Doc Fee on New</label>
                  <select
                    className="target-select"
                    value={frontEndTargets.docFee?.new?.enabled ? 'yes' : 'no'}
                    onChange={(e) => handleDocFeeChange('new', 'enabled', e.target.value === 'yes')}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div className="target-input-group">
                  <label>Amount per New Bike</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={docFeeInputs.new}
                    onChange={(e) => handleDocFeeAmountChange('new', e.target.value)}
                    onFocus={(e) => handleDocFeeAmountFocus('new', e)}
                    onBlur={() => handleDocFeeAmountBlur('new')}
                    placeholder="$0.00"
                    disabled={!frontEndTargets.docFee?.new?.enabled}
                  />
                </div>
                <div className="target-input-group">
                  <label>Charge a Doc Fee on Used</label>
                  <select
                    className="target-select"
                    value={frontEndTargets.docFee?.used?.enabled ? 'yes' : 'no'}
                    onChange={(e) => handleDocFeeChange('used', 'enabled', e.target.value === 'yes')}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                <div className="target-input-group">
                  <label>Amount per Used Bike</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={docFeeInputs.used}
                    onChange={(e) => handleDocFeeAmountChange('used', e.target.value)}
                    onFocus={(e) => handleDocFeeAmountFocus('used', e)}
                    onBlur={() => handleDocFeeAmountBlur('used')}
                    placeholder="$0.00"
                    disabled={!frontEndTargets.docFee?.used?.enabled}
                  />
                </div>
              </div>
            </div>

            {/* F&I Product Benchmarks — the bars behind the F&I Scoreboard's
                product cards. Rates rather than totals, so they're set once
                here instead of month by month above. */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">F&amp;I Product Benchmarks</h3>
                <button className="btn-save-targets" onClick={saveFrontEndTargets}>
                  Save Benchmarks
                </button>
              </div>
              <p className="auto-calculated-note">
                The bars each product is measured against on the F&amp;I Scoreboard.
                Penetration is the share of motorcycles sold that should take the
                product; Avg / Sold is what each one written should average. These are
                rates, not monthly totals, so they aren't prorated, and changing one
                restates past months. Clearing a field restores the industry default.
                Choose your benchmarks, then use Save Benchmarks above.
              </p>
              <div className="targets-grid fi-products-grid">
                {FI_PRODUCT_DEFAULTS.map(({ field, label }) => (
                  <React.Fragment key={field}>
                    <div className="target-input-group">
                      <label>{label} Penetration (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={frontEndTargets.fiProducts?.[field]?.penetrationTarget ?? ''}
                        onChange={(e) => handleFiProductChange(field, 'penetrationTarget', e.target.value)}
                        onBlur={() => handleFiProductBlur(field, 'penetrationTarget')}
                        placeholder="0"
                      />
                    </div>
                    <div className="target-input-group">
                      <label>{label} Avg / Sold ($)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatCurrency(frontEndTargets.fiProducts?.[field]?.averageTarget ?? 0)}
                        onChange={(e) => {
                          const numValue = e.target.value.replace(/[^0-9]/g, '');
                          handleFiProductChange(field, 'averageTarget', numValue);
                        }}
                        onBlur={() => handleFiProductBlur(field, 'averageTarget')}
                        placeholder="$0"
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Employee Allocations */}
            <div className="employee-allocations">
              <div className="allocation-header-row">
                <div>
                  <h3>Sales Person Target Allocations</h3>
                  <p className="allocation-info">Allocate the percentage of unit sales targets to each sales person (Total must equal 100%)</p>
                </div>
                <button className="btn-distribute" onClick={distributeEvenly}>
                  Distribute
                </button>
              </div>
              
              {allocationWarning && (
                <div className="allocation-warning">
                  Total allocation: {totalAllocation}% (should be 100%)
                </div>
              )}

              <div className="allocation-table">
                <div className="allocation-header">
                  <div className="allocation-name">Sales Person</div>
                  <div className="allocation-percentage">Target %</div>
                </div>
                
                {employees
                  .filter(employee => {
                    // Check if employee has Fit Specialist role in their roles array
                    const roles = Array.isArray(employee.roles) ? employee.roles : [];
                    return roles.includes('FIT_SPECIALIST') || roles.includes('Fit Specialist');
                  })
                  .map(employee => (
                  <div key={employee.id} className="allocation-row">
                    <div className="allocation-name">{employee.name}</div>
                    <div className="allocation-input">
                      <input 
                        type="number" 
                        min="0" 
                        max="100"
                        step="1"
                        value={frontEndTargets.employeeAllocations?.[employee.id] || 0}
                        onChange={(e) => handleEmployeeAllocationChange(employee.id, e.target.value)}
                        onBlur={() => handleEmployeeAllocationBlur(employee.id)}
                        onFocus={handleInputFocus}
                        placeholder="0"
                      />
                      <span className="percent">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="button-group">
              <button className="btn-save" onClick={saveFrontEndTargets}>
                Save Sales Targets
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Operations Targets Tab */}
      {activeTab === 'fixedops' && fixedOpsTargets && (
        <div className="tab-content">
          <div className="targets-section">
            <div className="section-header">
              <h2>Fixed Operations Targets</h2>
            </div>

            {/* Parts Monthly Targets */}
            <div className="targets-group">
              <div className="group-header">
                <h3 className="group-title">Parts - Monthly Targets</h3>
                <button className="btn-save-targets" onClick={saveFixedOpsTargets}>
                  Save Fixed Operations Targets
                </button>
              </div>
              <div className="monthly-targets-table">
                <div className="monthly-table-header">
                  <div className="monthly-header-cell">Month</div>
                  <div className="monthly-header-cell">Net Sales</div>
                  <div className="monthly-header-cell">Gross Profit</div>
                  <div className="monthly-header-cell">Gross Margin %</div>
                  <div className="monthly-header-cell">Transactions</div>
                  <div className="monthly-header-cell">Items</div>
                  <div className="monthly-header-cell">Inventory Value</div>
                </div>
                {fixedOpsTargets.monthlyTargets && getOrderedMonths().map((monthInfo, index) => {
                  const monthData = fixedOpsTargets.monthlyTargets.find(m => m.month === monthInfo.index);
                  const dataIndex = fixedOpsTargets.monthlyTargets.findIndex(m => m.month === monthInfo.index);

                  return (
                    <div key={`parts-${monthInfo.index}`} className="monthly-table-row">
                      <div className="monthly-cell month-name">{monthInfo.name}</div>

                      {/* Net Sales */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.partsNetSales === 0 ? '0' : formatCurrency(monthData?.partsNetSales || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'partsNetSales', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'partsNetSales')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Gross Profit */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.partsGrossProfit === 0 ? '0' : formatCurrency(monthData?.partsGrossProfit || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'partsGrossProfit', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'partsGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Gross Margin % */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          step="0.1"
                          value={monthData?.partsGrossMargin || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'partsGrossMargin', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'partsGrossMargin')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Transactions */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.partsTransactions || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'partsTransactions', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'partsTransactions')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Items (auto-calculated) */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.partsItems || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'partsItems', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'partsItems')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Inventory Value */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.partsInventoryValue === 0 ? '0' : formatCurrency(monthData?.partsInventoryValue || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'partsInventoryValue', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'partsInventoryValue')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Parts Inventory Turns */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Parts & Accessories - Inventory Turns Target Range</h3>
              </div>
              <p className="allocation-info" style={{ marginBottom: '16px' }}>
                Set the target range for annual inventory turns
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Minimum Turns</label>
                  <input
                    type="number"
                    step="0.1"
                    value={fixedOpsTargets.inventoryTurns?.partsMin || 0}
                    onChange={(e) => handleInventoryTurnsChange('partsMin', e.target.value)}
                    onBlur={() => handleInventoryTurnsBlur('partsMin')}
                    onFocus={handleInputFocus}
                    placeholder="3"
                  />
                </div>
                <div className="target-input-group">
                  <label>Maximum Turns</label>
                  <input
                    type="number"
                    step="0.1"
                    value={fixedOpsTargets.inventoryTurns?.partsMax || 0}
                    onChange={(e) => handleInventoryTurnsChange('partsMax', e.target.value)}
                    onBlur={() => handleInventoryTurnsBlur('partsMax')}
                    onFocus={handleInputFocus}
                    placeholder="5"
                  />
                </div>
              </div>
            </div>

            {/* Parts Transaction Benchmarks */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Parts &amp; Accessories - Transaction Benchmarks</h3>
              </div>
              <p className="allocation-info" style={{ marginBottom: '16px' }}>
                Benchmarks shown on the Profit Summary for parts transactions
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Average Line Items per Transaction</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={fixedOpsTargets.benchmarks?.partsLineItems ?? DEFAULT_BENCHMARKS.partsLineItems}
                    onChange={(e) => handleBenchmarkChange('partsLineItems', e.target.value)}
                    onBlur={() => handleBenchmarkBlur('partsLineItems')}
                    onFocus={handleInputFocus}
                    placeholder="3"
                  />
                </div>
                <div className="target-input-group">
                  <label>Average Dollars per Transaction ($)</label>
                  <input
                    type="text"
                    value={formatCurrency(fixedOpsTargets.benchmarks?.partsDollars ?? DEFAULT_BENCHMARKS.partsDollars)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleBenchmarkChange('partsDollars', numValue);
                    }}
                    onBlur={() => handleBenchmarkBlur('partsDollars')}
                    onFocus={handleInputFocus}
                    placeholder="$150"
                  />
                </div>
              </div>
            </div>

            {/* Service Monthly Targets */}
            <div className="targets-group">
              <div className="group-header">
                <h3 className="group-title">Service - Monthly Targets</h3>
              </div>
              <div className="monthly-targets-table">
                <div className="monthly-table-header">
                  <div className="monthly-header-cell">Month</div>
                  <div className="monthly-header-cell">Net Sales</div>
                  <div className="monthly-header-cell">Gross Profit</div>
                  <div className="monthly-header-cell">Gross Margin %</div>
                  <div className="monthly-header-cell">Transactions</div>
                  <div className="monthly-header-cell">Hours</div>
                  <div className="monthly-header-cell">Open ROs (Internal)</div>
                  <div className="monthly-header-cell">Open ROs (Customer)</div>
                </div>
                {fixedOpsTargets.monthlyTargets && getOrderedMonths().map((monthInfo, index) => {
                  const monthData = fixedOpsTargets.monthlyTargets.find(m => m.month === monthInfo.index);
                  const dataIndex = fixedOpsTargets.monthlyTargets.findIndex(m => m.month === monthInfo.index);

                  return (
                    <div key={`service-${monthInfo.index}`} className="monthly-table-row">
                      <div className="monthly-cell month-name">{monthInfo.name}</div>

                      {/* Net Sales */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.serviceNetSales === 0 ? '0' : formatCurrency(monthData?.serviceNetSales || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceNetSales', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceNetSales')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Gross Profit */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.serviceGrossProfit === 0 ? '0' : formatCurrency(monthData?.serviceGrossProfit || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceGrossProfit', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Gross Margin % */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          step="0.1"
                          value={monthData?.serviceGrossMargin || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceGrossMargin', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceGrossMargin')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Transactions */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.serviceTransactions || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceTransactions', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceTransactions')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Hours */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          step="0.1"
                          value={monthData?.serviceHours || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceHours', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceHours')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Open ROs (Internal) */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.serviceOpenROsInternal || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceOpenROsInternal', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceOpenROsInternal')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Open ROs (Customer) */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.serviceOpenROsCustomer || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'serviceOpenROsCustomer', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'serviceOpenROsCustomer')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Service Technicians */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Service Team</h3>
              </div>
              <p className="allocation-info" style={{ marginBottom: '16px' }}>
                Specify the number of service technicians, labor rate, and the service benchmarks at this dealership
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Number of Service Technicians</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={fixedOpsTargets.serviceTechnicians || 0}
                    onChange={(e) => handleServiceTechniciansChange(e.target.value)}
                    onBlur={handleServiceTechniciansBlur}
                    onFocus={handleInputFocus}
                    placeholder="0"
                  />
                </div>
                <div className="target-input-group">
                  <label>Labor Rate ($)</label>
                  <input
                    type="text"
                    value={fixedOpsTargets.laborRate === 0 ? '0' : formatCurrency(fixedOpsTargets.laborRate || 0)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleLaborRateChange(numValue);
                    }}
                    onBlur={handleLaborRateBlur}
                    onFocus={handleInputFocus}
                    placeholder="$0"
                  />
                </div>
                <div className="target-input-group">
                  <label>Parts to Labor Ratio Benchmark ($)</label>
                  <input
                    type="text"
                    value={formatCurrency(fixedOpsTargets.benchmarks?.partsToLaborRatio ?? DEFAULT_BENCHMARKS.partsToLaborRatio)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleBenchmarkChange('partsToLaborRatio', numValue);
                    }}
                    onBlur={() => handleBenchmarkBlur('partsToLaborRatio')}
                    onFocus={handleInputFocus}
                    placeholder="$125"
                  />
                </div>
                <div className="target-input-group">
                  <label>Hours Per Repair Order Benchmark</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={fixedOpsTargets.benchmarks?.serviceHoursPerRO ?? DEFAULT_BENCHMARKS.serviceHoursPerRO}
                    onChange={(e) => handleBenchmarkChange('serviceHoursPerRO', e.target.value)}
                    onBlur={() => handleBenchmarkBlur('serviceHoursPerRO')}
                    onFocus={handleInputFocus}
                    placeholder="3"
                  />
                </div>
              </div>
            </div>

            {/* Apparel & Licensing Monthly Targets */}
            <div className="targets-group">
              <div className="group-header">
                <h3 className="group-title">Apparel & Licensing - Monthly Targets</h3>
              </div>
              <div className="monthly-targets-table">
                <div className="monthly-table-header">
                  <div className="monthly-header-cell">Month</div>
                  <div className="monthly-header-cell">Net Sales</div>
                  <div className="monthly-header-cell">Gross Profit</div>
                  <div className="monthly-header-cell">Gross Margin %</div>
                  <div className="monthly-header-cell">Transactions</div>
                  <div className="monthly-header-cell">Items</div>
                  <div className="monthly-header-cell">Inventory Value</div>
                </div>
                {fixedOpsTargets.monthlyTargets && getOrderedMonths().map((monthInfo, index) => {
                  const monthData = fixedOpsTargets.monthlyTargets.find(m => m.month === monthInfo.index);
                  const dataIndex = fixedOpsTargets.monthlyTargets.findIndex(m => m.month === monthInfo.index);

                  return (
                    <div key={`apparel-${monthInfo.index}`} className="monthly-table-row">
                      <div className="monthly-cell month-name">{monthInfo.name}</div>

                      {/* Net Sales */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.apparelNetSales === 0 ? '0' : formatCurrency(monthData?.apparelNetSales || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'apparelNetSales', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'apparelNetSales')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Gross Profit */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.apparelGrossProfit === 0 ? '0' : formatCurrency(monthData?.apparelGrossProfit || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'apparelGrossProfit', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'apparelGrossProfit')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>

                      {/* Gross Margin % */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          step="0.1"
                          value={monthData?.apparelGrossMargin || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'apparelGrossMargin', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'apparelGrossMargin')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Transactions */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.apparelTransactions || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'apparelTransactions', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'apparelTransactions')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Items (auto-calculated) */}
                      <div className="monthly-cell">
                        <input
                          type="number"
                          value={monthData?.apparelItems || 0}
                          onChange={(e) => handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'apparelItems', e.target.value)}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'apparelItems')}
                          onFocus={handleInputFocus}
                          placeholder="0"
                        />
                      </div>

                      {/* Inventory Value */}
                      <div className="monthly-cell">
                        <input
                          type="text"
                          value={monthData?.apparelInventoryValue === 0 ? '0' : formatCurrency(monthData?.apparelInventoryValue || 0)}
                          onChange={(e) => {
                            const numValue = e.target.value.replace(/\D/g, '');
                            handleFixedOpsMonthlyTargetChange(dataIndex >= 0 ? dataIndex : index, 'apparelInventoryValue', numValue);
                          }}
                          onBlur={() => handleFixedOpsMonthlyTargetBlur(dataIndex >= 0 ? dataIndex : index, 'apparelInventoryValue')}
                          onFocus={handleInputFocus}
                          placeholder="$0"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Apparel Inventory Turns */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Apparel & Licensing - Inventory Turns Target Range</h3>
              </div>
              <p className="allocation-info" style={{ marginBottom: '16px' }}>
                Set the target range for annual inventory turns
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Minimum Turns</label>
                  <input
                    type="number"
                    step="0.1"
                    value={fixedOpsTargets.inventoryTurns?.apparelMin || 0}
                    onChange={(e) => handleInventoryTurnsChange('apparelMin', e.target.value)}
                    onBlur={() => handleInventoryTurnsBlur('apparelMin')}
                    onFocus={handleInputFocus}
                    placeholder="2"
                  />
                </div>
                <div className="target-input-group">
                  <label>Maximum Turns</label>
                  <input
                    type="number"
                    step="0.1"
                    value={fixedOpsTargets.inventoryTurns?.apparelMax || 0}
                    onChange={(e) => handleInventoryTurnsChange('apparelMax', e.target.value)}
                    onBlur={() => handleInventoryTurnsBlur('apparelMax')}
                    onFocus={handleInputFocus}
                    placeholder="4"
                  />
                </div>
              </div>
            </div>

            {/* Apparel Transaction Benchmarks */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Apparel &amp; Licensing - Transaction Benchmarks</h3>
              </div>
              <p className="allocation-info" style={{ marginBottom: '16px' }}>
                Benchmarks shown on the Profit Summary for apparel transactions
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Average Line Items per Transaction</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={fixedOpsTargets.benchmarks?.apparelLineItems ?? DEFAULT_BENCHMARKS.apparelLineItems}
                    onChange={(e) => handleBenchmarkChange('apparelLineItems', e.target.value)}
                    onBlur={() => handleBenchmarkBlur('apparelLineItems')}
                    onFocus={handleInputFocus}
                    placeholder="3"
                  />
                </div>
                <div className="target-input-group">
                  <label>Average Dollars per Transaction ($)</label>
                  <input
                    type="text"
                    value={formatCurrency(fixedOpsTargets.benchmarks?.apparelDollars ?? DEFAULT_BENCHMARKS.apparelDollars)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleBenchmarkChange('apparelDollars', numValue);
                    }}
                    onBlur={() => handleBenchmarkBlur('apparelDollars')}
                    onFocus={handleInputFocus}
                    placeholder="$100"
                  />
                </div>
              </div>
            </div>

            {/* Deal Add-on Targets */}
            <div className="targets-group" style={{ marginTop: '24px' }}>
              <div className="group-header">
                <h3 className="group-title">Average Deal Add-on Targets per Bike Sold</h3>
              </div>
              <p className="allocation-info" style={{ marginBottom: '16px' }}>
                Set target dollar amounts for add-ons per bike sold
              </p>
              <div className="targets-grid">
                <div className="target-input-group">
                  <label>Parts ($)</label>
                  <input
                    type="text"
                    value={fixedOpsTargets.dealAddOnTargets?.parts === 0 ? '0' : formatCurrency(fixedOpsTargets.dealAddOnTargets?.parts || 600)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleDealAddOnChange('parts', numValue);
                    }}
                    onBlur={() => handleDealAddOnBlur('parts')}
                    onFocus={handleInputFocus}
                    placeholder="$600"
                  />
                </div>
                <div className="target-input-group">
                  <label>Labor ($)</label>
                  <input
                    type="text"
                    value={fixedOpsTargets.dealAddOnTargets?.labor === 0 ? '0' : formatCurrency(fixedOpsTargets.dealAddOnTargets?.labor || 300)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleDealAddOnChange('labor', numValue);
                    }}
                    onBlur={() => handleDealAddOnBlur('labor')}
                    onFocus={handleInputFocus}
                    placeholder="$300"
                  />
                </div>
                <div className="target-input-group">
                  <label>Apparel & Licensing ($)</label>
                  <input
                    type="text"
                    value={fixedOpsTargets.dealAddOnTargets?.apparel === 0 ? '0' : formatCurrency(fixedOpsTargets.dealAddOnTargets?.apparel || 100)}
                    onChange={(e) => {
                      const numValue = e.target.value.replace(/\D/g, '');
                      handleDealAddOnChange('apparel', numValue);
                    }}
                    onBlur={() => handleDealAddOnBlur('apparel')}
                    onFocus={handleInputFocus}
                    placeholder="$100"
                  />
                </div>
              </div>
            </div>

            <div className="button-group">
              <button className="btn-save" onClick={saveFixedOpsTargets}>
                Save Fixed Operations Targets
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        onConfirm={handleConfirmSave}
        title="Incomplete Targets"
        message={warningMessage}
        confirmText="Save Anyway"
        cancelText="Cancel"
        confirmStyle="primary"
      />
    </div>
  );
}
